import * as React from 'react';
import { WorkspaceDataGrid, type WorkspaceGridColumn } from '@/components/ui/workspace';
import type { FrameworkArea } from '@/lib/api/discovery-frameworks';
import { cn } from '@/lib/utils/cn';
import type { EditorCtx } from './editor';
import { shown } from './framework-logic';
import { ChangeTag, InlineAdd } from './parts';

/**
 * The Areas tab (prototype `#frameworks`, table `Code / Area / Definition
 * of complete / Requirement library modules`). A row opens the area panel.
 * While editing, "Add area" closes the table: the label is all it asks for,
 * the code comes from the numbering service (rule 1).
 */

const getRowId = (a: FrameworkArea) => a.id;
const isRetired = (a: FrameworkArea) => a.status === 'retired';

const COLUMNS: WorkspaceGridColumn<FrameworkArea>[] = [
  {
    key: 'code',
    header: 'Code',
    width: '64px',
    cell: (a) => <span className="font-mono text-meta font-medium text-ink-700">{a.code}</span>,
  },
  {
    key: 'area',
    header: 'Area',
    width: 'minmax(140px, 0.8fr)',
    cell: (a) => (
      <>
        <div
          className={cn('font-medium', isRetired(a) ? 'text-ink-300 line-through' : 'text-ink-900')}
        >
          {a.label} <ChangeTag change={a.change} />
        </div>
        <div className="text-meta text-ink-500">{a.name}</div>
      </>
    ),
  },
  {
    key: 'definition',
    header: 'Definition of complete',
    width: 'minmax(200px, 1.7fr)',
    cell: (a) => (
      <span className={isRetired(a) ? 'text-ink-300 line-through' : 'text-ink-900'}>
        {a.definition}
      </span>
    ),
  },
  {
    key: 'modules',
    header: 'Requirement library modules',
    width: 'minmax(150px, 0.9fr)',
    text: false,
    cell: (a) => (
      <div className="flex flex-wrap gap-1 py-1.5">
        {a.library_modules.length > 0 ? (
          a.library_modules.map((m) => (
            <span
              key={m.id}
              // Grows with a long module name that wraps in the narrow column
              // ("Operations & Administrative Scheduling"): a fixed height let
              // the wrapped lines spill over the next chip (T-0192).
              className="inline-flex min-h-5 items-center rounded-full border border-ink-100 bg-paper-100 px-2 py-0.5 text-[11px] font-medium leading-4 text-ink-700"
            >
              {m.name ?? 'Unknown module'}
            </span>
          ))
        ) : (
          <span className="text-meta text-ink-500">None</span>
        )}
      </div>
    ),
  },
];

const rowClassName = (a: FrameworkArea) =>
  a.change === 'added' || a.change === 'changed'
    ? 'shadow-[inset_2px_0_0_var(--accent-amber-600)]'
    : undefined;

export function AreasTab({
  ctx,
  isCurator,
  selectedId,
  onOpen,
  onAdd,
}: {
  ctx: EditorCtx;
  isCurator: boolean;
  selectedId: string | null;
  onOpen: (id: string) => void;
  onAdd: (label: string) => Promise<React.ReactNode | null>;
}) {
  const { areas } = ctx.content;
  const rows = React.useMemo(() => shown(areas, ctx.editing), [areas, ctx.editing]);
  const [focusedId, setFocusedId] = React.useState<string | null>(selectedId);
  React.useEffect(() => {
    if (selectedId) setFocusedId(selectedId);
  }, [selectedId]);

  return (
    <WorkspaceDataGrid
      aria-label="Areas"
      rows={rows}
      getRowId={getRowId}
      columns={COLUMNS}
      wrap
      estimateRowHeight={72}
      focusedId={focusedId}
      onFocusChange={(id) => {
        setFocusedId(id);
        // Like the requirement library: an open panel follows row focus.
        if (selectedId) onOpen(id);
      }}
      onOpen={onOpen}
      rowClassName={rowClassName}
      globalKeys
      emptyState={
        <div className="px-6 py-14 text-center text-body-sm text-ink-500">
          <strong className="mb-1 block text-h3 font-semibold text-ink-900">No areas yet</strong>
          {ctx.editing
            ? 'Add the first area below.'
            : isCurator
              ? 'Select Edit to add areas.'
              : 'Nothing has been published in this framework yet.'}
        </div>
      }
      footerRow={
        ctx.editing ? (
          <InlineAdd
            trigger="row"
            label="Add area"
            inputLabel="New area label"
            placeholder="Short label, e.g. Fixed assets"
            busy={ctx.busy}
            onAdd={onAdd}
          />
        ) : undefined
      }
    />
  );
}
